/**
 * Attribution for visits that come from AI assistants and answer engines.
 *
 * These arrive largely invisible. AI engines link out with `noreferrer`, so
 * Clarity's referrer dimension is empty for essentially all of our traffic, and
 * only ChatGPT identifies itself — it appends `utm_source=chatgpt.com`, which is
 * the sole reason we can see that channel at all. Perplexity, Copilot, Gemini
 * and the rest currently land in the same anonymous bucket as direct traffic.
 *
 * So we detect what we can from the URL and, where present, the referrer, and
 * tag the session ourselves.
 */

export interface AiSource {
  /** Stable machine id used as the analytics tag value. */
  id: string;
  label: string;
  /** How we worked it out — useful when judging how much to trust the number. */
  via: 'utm' | 'referrer';
}

/** Hosts that are genuinely an AI assistant, keyed to a stable id. */
const AI_HOSTS: { domains: string[]; id: string; label: string }[] = [
  { domains: ['chatgpt.com', 'chat.openai.com', 'openai.com'], id: 'chatgpt', label: 'ChatGPT' },
  { domains: ['perplexity.ai'], id: 'perplexity', label: 'Perplexity' },
  { domains: ['gemini.google.com', 'bard.google.com'], id: 'gemini', label: 'Gemini' },
  { domains: ['copilot.microsoft.com'], id: 'copilot', label: 'Microsoft Copilot' },
  { domains: ['claude.ai'], id: 'claude', label: 'Claude' },
  { domains: ['meta.ai'], id: 'meta-ai', label: 'Meta AI' },
  { domains: ['grok.com', 'x.ai'], id: 'grok', label: 'Grok' },
  { domains: ['you.com'], id: 'you', label: 'You.com' },
  { domains: ['poe.com'], id: 'poe', label: 'Poe' },
  { domains: ['phind.com'], id: 'phind', label: 'Phind' },
];

/**
 * Substrings we accept inside utm_source / ref, which are set by the engine
 * rather than by us and so vary in form ("chatgpt.com", "openai", "perplexity").
 */
const UTM_MATCHES: { needles: string[]; id: string; label: string }[] = [
  { needles: ['chatgpt', 'openai'], id: 'chatgpt', label: 'ChatGPT' },
  { needles: ['perplexity'], id: 'perplexity', label: 'Perplexity' },
  { needles: ['gemini', 'bard'], id: 'gemini', label: 'Gemini' },
  { needles: ['copilot'], id: 'copilot', label: 'Microsoft Copilot' },
  { needles: ['claude', 'anthropic'], id: 'claude', label: 'Claude' },
  { needles: ['meta.ai', 'metaai'], id: 'meta-ai', label: 'Meta AI' },
  { needles: ['grok'], id: 'grok', label: 'Grok' },
  { needles: ['phind'], id: 'phind', label: 'Phind' },
];

/**
 * Deliberately NOT treated as AI traffic: google.com, bing.com and
 * duckduckgo.com. A visit from a Google AI Overview carries a plain google.com
 * referrer, indistinguishable from an ordinary organic click — counting those
 * would inflate the AI number with regular search. AI Overview traffic is
 * therefore not measurable here, and should not be claimed as measured.
 */
function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** Pure detection, so it can be tested without a browser. */
export function detectAiSource(url: string, referrer: string): AiSource | null {
  // A URL parameter is the stronger signal: the engine set it deliberately, and
  // it survives the referrer being stripped.
  try {
    const params = new URL(url).searchParams;
    const claimed = (params.get('utm_source') || params.get('ref') || '').toLowerCase();
    if (claimed) {
      for (const { needles, id, label } of UTM_MATCHES) {
        if (needles.some((n) => claimed.includes(n))) return { id, label, via: 'utm' };
      }
    }
  } catch {
    // Malformed URL — fall through to the referrer.
  }

  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      for (const { domains, id, label } of AI_HOSTS) {
        if (domains.some((d) => hostMatches(host, d))) return { id, label, via: 'referrer' };
      }
    } catch {
      // Not a parseable referrer — nothing to attribute.
    }
  }

  return null;
}

const STORAGE_KEY = 'kundaliai_ai_source';

/**
 * The AI source for this session, detected on the landing page and remembered
 * afterwards — the referrer and utm only exist on the first page view, but the
 * whole session should stay attributed to it.
 */
export function resolveAiSource(): AiSource | null {
  if (typeof window === 'undefined') return null;

  const detected = detectAiSource(window.location.href, document.referrer);
  if (detected) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(detected));
    } catch {
      // Private mode or blocked storage — attribution just won't persist
      // across navigations in this session.
    }
    return detected;
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as AiSource) : null;
  } catch {
    return null;
  }
}
